import {
  SURVEY_DIALOG_MAX_WIDTH,
  SurveyContent,
} from "@bezoned/shared/components/survey/survey-content.jsx";
import type {
  SurveyAnswers,
  SurveyDefinition,
} from "@bezoned/shared/schemas/survey/survey.schema.js";
import { Dialog, DialogSurface } from "@fluentui/react-components";
import { useState } from "react";

import type { SharedModalProps } from "./base-modal.js";

const ratingSurveyContent = {
  behavior: {
    showCloseButton: true,
    submitLabel: null,
  },
  content: {
    items: [
      {
        align: "center",
        fontSize: "header",
        id: "72201e17-f111-4651-9752-99fbd54c7bde",
        text: "We're on a mission to make remote and hybrid work better, and we hope you'll help.",
        type: "text",
      },
      {
        align: "center",
        fontSize: "text",
        id: "648d933b-b8e3-4a3f-83f2-060e44a2e480",
        text: "So, if BeZoned has helped you feel more connected, would you share that with others?",
        type: "text",
      },
      {
        align: "center",
        fontSize: "text",
        id: "edb714dd-6771-44ec-b3df-7f371fc57ee5",
        text: "It only takes a few words or even just a quick rating. Sharing your honest experience helps other teams facing the same challenge understand what BeZoned could do for them\n- and we're a small team, so every voice counts.",
        type: "text",
      },
      {
        align: "center",
        fontSize: "header",
        id: "1ca6dc6a-e216-47fe-9933-c1051485e455",
        text: "Thanks 😊",
        type: "text",
      },
      {
        icon: "Microsoft",
        id: "72e89e71-1983-4548-9cc0-a15648b95e08",
        link: "https://marketplace.microsoft.com/en-us/product/WA200009345?tab=Reviews",
        text: "Review on Microsoft",
        type: "button",
        variant: "filled",
      },
      {
        icon: "Google",
        id: "f48e3f26-48e8-4876-9ee0-335db7fdd77b",
        link: "https://g.page/r/CcJjebaH4WXFEBI/review",
        text: "Review on Google",
        type: "button",
        variant: "outlined",
      },
    ],
  },
  general: {
    imageUrl:
      "https://bezonedstagingblob.blob.core.windows.net/bezoned-staging-surveys/bz_hearts.jpg",
    title: "Enjoying your time in the zone?",
  },
} satisfies {
  behavior: Pick<
    SurveyDefinition["behavior"],
    "showCloseButton" | "submitLabel"
  >;
  content: SurveyDefinition["content"];
  general: Pick<SurveyDefinition["general"], "imageUrl" | "title">;
};

export type SurveyModalProps = SharedModalProps & {
  // The eligible survey definition returned by the API.
  survey: SurveyDefinition;
  // Whether the snooze action is currently available for this viewer.
  showSnoozeButton?: boolean;
  // Whether a submit/snooze/skip request is currently in flight.
  submitting?: boolean;
  // Persist the collected answers (status = filled).
  onSubmit?: (answers: SurveyAnswers) => void;
  // Snooze the survey (status = snoozed).
  onSnooze?: () => void;
  // Skip the survey (status = skipped). Triggered by the close button.
  onSkip?: () => void;
  // Close the fixed rating page without recording another survey action.
  onRatingClose?: () => void;
};

export const SurveyModal = ({
  survey,
  showSnoozeButton,
  submitting,
  onSubmit,
  onSnooze,
  onRatingClose,
  onSkip,
  onClose,
}: SurveyModalProps) => {
  const [showRatingPage, setShowRatingPage] = useState(false);

  // Closing via the top-right button is recorded as a skip. Fallbacks preserve
  // safe modal behavior for previews/tests that do not pass persistence hooks.
  const handleSkip = (): void => {
    (onSkip ?? onClose)?.();
  };
  const handleSnooze = (): void => {
    (onSnooze ?? onClose)?.();
  };
  const handleSubmit = (answers: SurveyAnswers): void => {
    if (survey.behavior.askForRating) {
      setShowRatingPage(true);
    }

    onSubmit?.(answers);
  };

  const title = showRatingPage
    ? ratingSurveyContent.general.title
    : survey.general.title;

  return (
    <Dialog
      aria-label={title || "Untitled survey"}
      modalType="alert"
      open={true}
    >
      <DialogSurface
        className="m-auto! max-h-[100dvh] w-[calc(100vw-2rem)] overflow-y-auto border-none! bg-transparent! p-0! shadow-none! md:max-h-[80dvh]"
        style={{ maxWidth: SURVEY_DIALOG_MAX_WIDTH }}
      >
        {showRatingPage ? (
          <SurveyContent
            content={ratingSurveyContent}
            onClose={() => (onRatingClose ?? onClose)?.()}
            showSnoozeButton={false}
            submitting={false}
            variant="modal"
          />
        ) : (
          <SurveyContent
            content={{
              behavior: {
                showCloseButton: survey.behavior.showCloseButton,
                submitLabel: survey.behavior.submitLabel,
              },
              content: survey.content,
              general: {
                imageUrl: survey.general.imageUrl,
                title: survey.general.title,
              },
            }}
            onClose={handleSkip}
            onSnooze={handleSnooze}
            onSubmit={handleSubmit}
            showSnoozeButton={showSnoozeButton ?? false}
            submitting={submitting ?? false}
            variant="modal"
          />
        )}
      </DialogSurface>
    </Dialog>
  );
};
